import { useState, useEffect, useRef } from "react";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { sendMagicLink } from "../api/auth";

const RESEND_COOLDOWN_SECONDS = 60;
const POLL_INTERVAL_MS = 3000;

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { currentUser, accountStatus } = useAuth();
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (accountStatus === "trial" || accountStatus === "active") {
      navigate("/onboarding", { replace: true });
    }
  }, [accountStatus, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      if (currentUser) {
        await currentUser.reload();
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentUser]);

  const handleResend = async () => {
    if (!currentUser?.email || cooldown > 0) return;
    setError(null);
    setLoading(true);
    try {
      await sendMagicLink(currentUser.email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError("Failed to resend verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "background.default",
      }}
    >
      <Box sx={{ maxWidth: 480, width: "100%", p: 4, textAlign: "center" }}>
        <Typography variant="h3" sx={{ mb: 2 }}>Verify your email</Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          We sent a verification link to
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
          {currentUser?.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The link expires in 1 hour. Click it to activate your account.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Button
          onClick={handleResend}
          disabled={cooldown > 0 || loading}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
        >
          {loading
            ? <CircularProgress size={20} />
            : cooldown > 0
            ? `Resend in ${cooldown}s`
            : "Resend verification email"}
        </Button>

        <Button variant="text" onClick={() => navigate("/login")}>
          Sign in with a different account
        </Button>
      </Box>
    </Box>
  );
}
